import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { useLabels, usePrefs, useRequestData } from 'ws-scripts/modules/common';
import { DrawerContext } from '../context/DrawerContext';
import { CurrentPathProvider } from '../context/CurrentPathContext';
import { DrawerNavigationProvider } from '../context/DrawerNavigationContext';
import fireModulesRefresh from '../utils/fireModulesRefresh';
import useTrackEvent from '../hooks/useTrackEvent';
import {
	DRAWER_EVENT,
	CLOSE_METHOD,
	TRACKING_CONFIG,
	WIDGET_STATE
} from '../utils/trackEvent';
import HamburgerButton from './HamburgerButton';
import FeaturedNavBar from './FeaturedNavBar';
import MyCarsHeaderIcon from './MyCarsHeaderIcon';
import HeaderSearchButton from './HeaderSearchButton';
import { SSRDrilldownLinks } from './SSRDrilldownLinks';
import { DrilldownPanel } from './DrilldownPanel';
import { MainPanel } from './MainPanel';
import { SSRNavPanel } from './SSRNavPanel';
import { DrawerScrollLock } from './DrawerScrollLock';
import { DEVICE_TYPE_MOBILE } from '../global-constants';

/**
 * NavigationDrawer — renders the main drawer panel as a keep-mounted SlidePanel
 * (via MainPanel) docked left/right.
 *
 * SlidePanel portals to document.body and toggles visibility instead of
 * unmounting on close, so embedded fragment widgets never re-bootstrap on
 * reopen. Page scroll-lock is owned by a single DrawerScrollLock (keyed off the
 * whole drawer family's open state) rather than the react-bootstrap
 * ModalManager. DrawerPanelBody provides the main drawer content (nav items,
 * search, CTA buttons). The drilldown submenu is rendered separately.
 *
 * @returns {React.ReactElement}
 */
const NavigationDrawer = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [mounted, setMounted] = useState(false);
	const hamburgerRef = useRef(null);
	const closeRef = useRef(null);
	const searchRef = useRef(null);
	const closeMegaMenuRef = useRef(null);
	const labels = useLabels();
	const { drawerDirection = 'right' } = usePrefs();
	const requestData = useRequestData();
	const isMobile = requestData?.deviceType === DEVICE_TYPE_MOBILE;
	const title = labels.get('NAV_DRAWER_TITLE');
	const dockClass = drawerDirection === 'left' ? 'dock-left' : 'dock-right';
	const directionClass =
		drawerDirection === 'left'
			? 'ws-nav-drawer--left'
			: 'ws-nav-drawer--right';

	useEffect(() => setMounted(true), []);

	// Open/close tracking lives here rather than in the trigger components
	// (HamburgerButton, PanelHeader, SlidePanel) because this is the only place
	// that owns `isOpen`. Centralizing it is what makes two guarantees possible:
	// the open duration on `drawerClose` is measured against the same state
	// transition the user saw, and neither event can double-fire when a trigger
	// runs while the drawer is already in the target state. Trigger components
	// stay presentational and only report HOW the drawer was dismissed.
	const openedAtRef = useRef(null);
	const track = useTrackEvent();

	const openDrawer = useCallback(() => {
		closeMegaMenuRef.current?.();
		// Unconditional, and deliberately ahead of the guard below: the guard is
		// about analytics, not about state. `isOpen` here is the value captured when
		// this callback was created, so gating the state update on it would make
		// openDrawer/closeDrawer lose their update whenever two of them run in one
		// commit — a silent behavior bug in exchange for nothing, since setting the
		// state to the value it already holds is a no-op React drops anyway.
		setIsOpen(true);
		// Re-opening an already-open drawer is not a new open: no second
		// `drawerOpen`, and the open-duration clock keeps its original start.
		if (isOpen) return;
		openedAtRef.current = Date.now();
		track(DRAWER_EVENT.DRAWER_OPEN, {
			element: TRACKING_CONFIG.hamburgerEl,
			result: TRACKING_CONFIG.drawerOpenedResult,
			// The drawer was shut when the user clicked, so the state *before* the
			// action is the default one.
			widgetState: WIDGET_STATE.DEFAULT,
			direction: drawerDirection
		});
	}, [isOpen, track, drawerDirection]);

	/**
	 * @param {string} [closeMethod] - one of CLOSE_METHOD. Coerced to the close
	 *   button default when a caller wires this straight to a DOM handler and
	 *   hands us the event object instead of a method string.
	 */
	const closeDrawer = useCallback(
		(closeMethod) => {
			// Unconditional and ahead of the guard, for the reason given in
			// openDrawer: closing is idempotent, reporting a close is not.
			setIsOpen(false);
			// No close event while the drawer is already shut: Escape and the
			// dismiss scrim stay wired to the closed panel, so without this guard
			// they would emit phantom `drawerClose` events with a null duration.
			if (!isOpen) return;
			const resolvedCloseMethod =
				typeof closeMethod === 'string'
					? closeMethod
					: CLOSE_METHOD.CLOSE_BUTTON;
			track(DRAWER_EVENT.DRAWER_CLOSE, {
				// Method is in `element` as well as `closeMethod` so the dismissal
				// breakdown survives the GA path, which keeps only element/action
				// /result. See TRACKING_CONFIG.
				element: TRACKING_CONFIG.getCloseEl(resolvedCloseMethod),
				result: TRACKING_CONFIG.drawerClosedResult,
				widgetState: WIDGET_STATE.DRAWER_OPEN,
				closeMethod: resolvedCloseMethod,
				openDurationMs:
					openedAtRef.current === null
						? null
						: Date.now() - openedAtRef.current
			});
			openedAtRef.current = null;
		},
		[isOpen, track]
	);

	const contextValue = useMemo(
		() => ({ isOpen, openDrawer, closeDrawer }),
		[isOpen, openDrawer, closeDrawer]
	);

	const handleExited = useCallback(() => {
		// Scroll-lock teardown (the scrollbar-width var + body.modal-open) is now
		// owned by DrawerScrollLock via useBodyScrollLock — it locks/unlocks
		// atomically off the drawer family's open state, so nothing to undo here.
		// This callback only returns focus to the hamburger after the slide-out.
		hamburgerRef.current?.focus();
	}, []);

	const handleEntered = useCallback(() => {
		closeRef.current?.focus();
		// Scoped to lazyImage only — passing the full window.DDC.modules dict
		// is equivalent to an unscoped refresh (CMS default.js falls back to
		// DDC.modules when data.modules is absent, and here data.modules IS
		// DDC.modules), which re-runs loadConditional for every module on the
		// page, including ones whose init assumptions don't hold outside of
		// normal page load (e.g. map-dynamic's loadConditional throws if
		// re-triggered before its jquery.inview dependency has loaded).
		fireModulesRefresh({ lazyImage: window.DDC?.modules?.lazyImage });
	}, []);

	// Swaps static phone numbers in the drawer's DOM for dealer-tracked numbers
	// every time the drawer opens. Two passes: (1) fix up any data-phone-ref
	// that resolved to the wrong tracking number before this widget rendered
	// (see the mismatch-correction pass below), then (2) run the phone-swapping
	// library's own DOM traversal so the drawer's content — invisible to it
	// until now — gets swapped like the rest of the page.
	useEffect(() => {
		if (!isOpen) return;
		if (typeof window === 'undefined') return;

		const ps = window.phoneSwapping;
		if (!ps?.domTraverse || !ps?.swapNumbers) return;

		// Before traversal: fix data-phone-ref mismatches by scanning lookupCache.
		// When dept.type (e.g. "SALES_GROUP") doesn't match the artifact key (e.g.
		// "SALES"), the library caches null for the mismatched key. Other widgets on
		// the page (e.g. header nav) may have already resolved the same phone number
		// under the correct key. Find that entry and update data-phone-ref + href
		// directly — the <a> is already in processedNodes so domTraverse won't
		// re-process it, but the text node inside the <span> child is not yet
		// processed and will read the corrected data-phone-ref.
		if (ps.lookupCache && ps.cleanNumber) {
			document
				.querySelectorAll(
					'a[data-phone-ref][data-account-ref][data-drawer-phone]'
				)
				.forEach((link) => {
					const phoneRef = link.getAttribute('data-phone-ref');
					const acctRef = link.getAttribute('data-account-ref');
					const rawHref = link.getAttribute('href') || '';
					if (!rawHref.startsWith('tel:') || !acctRef || !phoneRef)
						return;

					const cleanPhone = ps.cleanNumber(rawHref.slice(4));
					const currentKey = `${cleanPhone}_${phoneRef}_${acctRef}`;

					// Skip if the current phone-ref already resolved a tracking number.
					if (ps.lookupCache[currentKey]) return;

					// Scan for a non-null cache entry with the same phone + acctId.
					const prefix = `${cleanPhone}_`;
					const suffix = `_${acctRef}`;
					const correctKey = Object.keys(ps.lookupCache).find(
						(k) =>
							k.startsWith(prefix) &&
							k.endsWith(suffix) &&
							ps.lookupCache[k]
					);
					if (!correctKey) return;

					// Update data-phone-ref to the dept key that actually resolves.
					const correctDept = correctKey.slice(
						prefix.length,
						-suffix.length
					);
					link.setAttribute('data-phone-ref', correctDept);

					// Update href directly — domTraverse won't revisit this <a>.
					const trackingNumber = ps.lookupCache[correctKey];
					link.setAttribute(
						'href',
						`tel:${String(trackingNumber).replace(/\s*/gi, '')}`
					);
				});
		}

		// Full traversal catches text nodes that observedNodeTraverse skipped.
		// Text nodes in <span> children of <a> are not in processedNodes; they
		// read the (now-corrected) data-phone-ref from their <a> ancestor.
		ps.domTraverse(document.body, ps.swapNumbers.bind(ps));
	}, [isOpen]);

	return (
		<DrawerContext.Provider value={contextValue}>
			<CurrentPathProvider>
				<DrawerNavigationProvider>
					<DrawerScrollLock />
					<div
						className={`ws-nav-drawer ${directionClass} h-100 d-flex align-items-center`}
						data-testid="nav-drawer-root"
					>
						<div className="ws-nav-drawer__header-row d-flex align-items-center">
							{!isMobile && (
								<FeaturedNavBar
									closeMegaMenuRef={closeMegaMenuRef}
								/>
							)}
							<HeaderSearchButton searchRef={searchRef} />
							<MyCarsHeaderIcon />
							<HamburgerButton ref={hamburgerRef} />
						</div>
					</div>

					{mounted ? (
						<MainPanel
							isOpen={isOpen}
							closeDrawer={closeDrawer}
							handleExited={handleExited}
							handleEntered={handleEntered}
							dockClass={dockClass}
							title={title}
							closeRef={closeRef}
							searchRef={searchRef}
							isMobile={isMobile}
						/>
					) : (
						<SSRNavPanel
							dockClass={dockClass}
							title={title}
							closeRef={closeRef}
							searchRef={searchRef}
						/>
					)}

					{mounted && (
						<DrilldownPanel
							dockClass={dockClass}
							drawerDirection={drawerDirection}
							isMobile={isMobile}
						/>
					)}
				</DrawerNavigationProvider>
			</CurrentPathProvider>
		</DrawerContext.Provider>
	);
};

export default NavigationDrawer;
export { SSRDrilldownLinks };
